import {
  Box,
  Button,
  Container,
  Paper,
  PasswordInput,
  PasswordInputProps,
  Stack,
  Text,
  TextInput,
  TextInputProps,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconLock, IconMail } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/img/logo.png";
import { Colors, HttpCodes } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  validateEmail,
} from "../../lib/methods";
import { NonPaginatedResponse } from "../../lib/types";
import { PerformRequest } from "../../lib/usePerformRequest";

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLoggingIn, setLoggingIn] = useState(false);

  const loginForm = useForm({
    initialValues: { email: "", password: "" },
    validate: {
      email: (value) => (validateEmail(value) ? null : "Enter a valid email"),
      password: (value) => (value.length > 0 ? null : "Password is required"),
    },
  });

  async function HandleLogin() {
    try {
      const { hasErrors } = loginForm.validate();
      if (!hasErrors) {
        const { email, password } = loginForm.values;
        setLoggingIn(true);
        const r = await PerformRequest<NonPaginatedResponse<{ token: string }>>(
          {
            method: Endpoints.GetLoginAdmin().method,
            route: Endpoints.GetLoginAdmin().route,
            data: {
              email,
              password,
            },
            onError: () => {
              setLoggingIn(false);
            },
          }
        );
        setLoggingIn(false);
        if (r) {
          if (r.status === HttpCodes.Success) {
            DefaultSuccessNotification("Login successful!");
            localStorage.setItem("t", r.data?.data?.token ?? "");
            navigate("/d");
          } else if (r.status === HttpCodes.Unauthorized) {
            DefaultErrorNotification("Invalid email or password!");
          }
        } else {
          DefaultErrorNotification(r?.message ?? "Unable to login!");
        }
      }
    } catch (e) {
      DefaultErrorNotification("Unable to login!");
    }
  }

  const inputProps: TextInputProps & PasswordInputProps = {
    radius: "md",
    size: "md",
    styles: {
      label: {
        marginBottom: "3px",
        fontSize: "14px",
      },
    },
  };
  return (
    <Container
      size="xs"
      style={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
    >
      <Box style={{ width: "100%" }}>
        <Paper
          shadow="md"
          p="xl"
          radius="lg"
          style={{ border: "1px solid #e9ecef" }}
        >
          <Stack gap="xl">
            <Stack gap="md" align="center">
              <img
                src={logo}
                alt="U-Family Medicine Logo"
                style={{
                  maxWidth: "140px",
                  height: "auto",
                  marginBottom: "1rem",
                }}
              />
              <Title order={2} fw={700} c="dark.8">
                Welcome Back
              </Title>
              <Text c="dimmed" size="sm" ta="center">
                Sign in to your admin account
              </Text>
            </Stack>

            <Stack gap="md">
              <TextInput
                label="Email"
                placeholder="admin@ufamilymedicine.com"
                leftSection={<IconMail size={18} />}
                {...loginForm.getInputProps("email")}
                {...inputProps}
              />

              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                leftSection={<IconLock size={18} />}
                {...loginForm.getInputProps("password")}
                {...inputProps}
              />

              <Button
                color={Colors.BluePrimary}
                onClick={HandleLogin}
                loading={isLoggingIn}
                size="md"
                radius="md"
                fullWidth
                mt="lg"
              >
                Sign In
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
